import {
  ActionAccordionExpandMode,
  ActionAccordionRowData,
  ActionAccordionSelectionMode,
} from '@/types/model/actionAccordion';

const hasMaterializedChildren = (row: ActionAccordionRowData): boolean => {
  return Array.isArray(row.children) && row.children.length > 0;
};

const hasMaterializedContent = (row: ActionAccordionRowData): boolean => {
  return row.content !== undefined && row.content !== null;
};

const isExpandableMaterializedRow = (row: ActionAccordionRowData): boolean => {
  return hasMaterializedContent(row) || hasMaterializedChildren(row);
};

export const mapRows = (
  rows: ActionAccordionRowData[],
  mapper: (row: ActionAccordionRowData, pathIds: number[]) => ActionAccordionRowData,
  parentPathIds: number[] = []
): ActionAccordionRowData[] => {
  return rows.map((row) => {
    const pathIds = [...parentPathIds, row.id];
    const nextChildren = row.children ? mapRows(row.children, mapper, pathIds) : undefined;
    const nextRow = nextChildren ? { ...row, children: nextChildren } : { ...row };

    return mapper(nextRow, pathIds);
  });
};

export const getSelectedRows = (rows: ActionAccordionRowData[]): ActionAccordionRowData[] => {
  const selected: ActionAccordionRowData[] = [];

  const visit = (currentRows: ActionAccordionRowData[]) => {
    for (const row of currentRows) {
      if (row.isSelected) {
        selected.push(row);
      }
      if (row.children && row.children.length > 0) {
        visit(row.children);
      }
    }
  };

  visit(rows);
  return selected;
};

export const findRowById = (rows: ActionAccordionRowData[], id: number): ActionAccordionRowData | null => {
  for (const row of rows) {
    if (row.id === id) {
      return row;
    }
    if (row.children && row.children.length > 0) {
      const found = findRowById(row.children, id);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

export const findRowPathById = (rows: ActionAccordionRowData[], id: number): number[] | null => {
  const visit = (currentRows: ActionAccordionRowData[], pathIds: number[]): number[] | null => {
    for (const row of currentRows) {
      const nextPath = [...pathIds, row.id];
      if (row.id === id) {
        return nextPath;
      }

      if (row.children && row.children.length > 0) {
        const found = visit(row.children, nextPath);
        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  return visit(rows, []);
};

export const toggleRowExpanded = (
  rows: ActionAccordionRowData[],
  rowId: number,
  expandMode: ActionAccordionExpandMode
): ActionAccordionRowData[] => {
  const toggleInLevel = (levelRows: ActionAccordionRowData[]): ActionAccordionRowData[] => {
    let hasTarget = false;

    const nextLevel = levelRows.map((row) => {
      if (row.id === rowId) {
        hasTarget = true;
        return { ...row, isExpanded: !row.isExpanded };
      }

      if (row.children && row.children.length > 0) {
        const nextChildren = toggleInLevel(row.children);
        if (nextChildren !== row.children) {
          hasTarget = true;
          return { ...row, children: nextChildren };
        }
      }

      return row;
    });

    if (!hasTarget) {
      return levelRows;
    }

    if (expandMode !== 'single-per-level') {
      return nextLevel;
    }

    const targetRow = nextLevel.find((row) => row.id === rowId);
    if (!targetRow || !targetRow.isExpanded) {
      return nextLevel;
    }

    return nextLevel.map((row) => {
      if (row.id === rowId) {
        return row;
      }

      return row.isExpanded ? { ...row, isExpanded: false } : row;
    });
  };

  return toggleInLevel(rows);
};

const setDescendantSelection = (row: ActionAccordionRowData, isSelected: boolean): ActionAccordionRowData => {
  const nextChildren = row.children?.map((child) => setDescendantSelection(child, isSelected));
  return {
    ...row,
    isSelected,
    children: nextChildren,
  };
};

const setVisibleDescendantSelection = (row: ActionAccordionRowData, isSelected: boolean): ActionAccordionRowData => ({
  ...row,
  isSelected,
  children: row.children?.map((child) => {
    if (!child.isSelectable) return child;
    return setVisibleDescendantSelection(child, isSelected);
  }),
});

const syncVisibleParentSelection = (row: ActionAccordionRowData): ActionAccordionRowData => {
  const children = row.children?.map(syncVisibleParentSelection);
  if (!children || children.length === 0 || !row.isSelectable) {
    return children ? { ...row, children } : row;
  }

  const selectableChildren = children.filter((child) => child.isSelectable);
  return {
    ...row,
    children,
    isSelected: selectableChildren.length > 0 && selectableChildren.every((child) => child.isSelected),
  };
};

export const setRowSelection = (
  rows: ActionAccordionRowData[],
  rowId: number,
  isSelected: boolean,
  selectionMode: ActionAccordionSelectionMode
): ActionAccordionRowData[] => {
  const nextRows = rows.map((row) => {
    if (row.id === rowId) {
      if (selectionMode === 'cascade-descendants') {
        return setDescendantSelection(row, isSelected);
      }
      if (selectionMode === 'cascade-visible') {
        return setVisibleDescendantSelection(row, isSelected);
      }
      return { ...row, isSelected };
    }

    if (row.children && row.children.length > 0) {
      return {
        ...row,
        children: setRowSelection(row.children, rowId, isSelected, selectionMode),
      };
    }

    return row;
  });

  return selectionMode === 'cascade-visible' ? nextRows.map(syncVisibleParentSelection) : nextRows;
};

export const expandAllMaterializedRows = (rows: ActionAccordionRowData[]): ActionAccordionRowData[] => {
  return mapRows(rows, (row) => {
    if (!isExpandableMaterializedRow(row)) {
      return row;
    }

    return row.isExpanded ? row : { ...row, isExpanded: true };
  });
};

export const collapseAllRows = (rows: ActionAccordionRowData[]): ActionAccordionRowData[] => {
  return mapRows(rows, (row) => {
    return row.isExpanded ? { ...row, isExpanded: false } : row;
  });
};

export const getContentCount = (row: ActionAccordionRowData): number => {
  if (!row.children || row.children.length === 0) {
    return row.contentCount ?? 1;
  }

  return row.children.reduce((count, child) => count + getContentCount(child), 0);
};
